import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { authService } from "../../services/auth.service";
import Button from "../Button/Button";
import PinField from "react-pin-field";
// import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { FacebookDotIcon, GoogleIcon, LogoMiniIcon, PlusIcon } from "../icons/SVGs";
import { profileUpdate } from "../../redux/slices/userSlice";
import { useDispatch } from "react-redux";
import { generateSecureToken } from "../../utils/navigateEditor";

const LinkAccountModal = ({ setstate1, email, provider }) => {
  const [loading, setLoading] = useState(false);
  const [pin, setPin] = useState("");
  const [countdown, setCountdown] = useState(60);
  const [isResending, setIsResending] = useState(false);
  // const { executeRecaptcha } = useGoogleReCaptcha();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    let interval;
    if (isResending) {
      interval = setInterval(() => {
        setCountdown((prevCountdown) => prevCountdown - 1);
      }, 1000);
    }
    return () => {
      clearInterval(interval);
    };
  }, [isResending]);

  useEffect(() => {
    if (countdown === 0) setIsResending(false);
  }, [countdown]);

  const handleVerifyOTP = async (pin) => {
    try {
      setLoading(true);

      // Validate PIN first
      if (!pin || pin.length !== 4 || !/^\d{4}$/.test(pin)) {
        setLoading(false);
        toast.error(t("Please enter a valid 4-digit PIN"));
        return;
      }

      if (!email) {
        setLoading(false);
        toast.error(t("Email is required"));
        return;
      }

      const token = generateSecureToken();

      if (!token) {
        setLoading(false);
        toast.error(t("Error occurred while submitting form"));
        return;
      }

      const device_id = Cookies.get("device_id");
      const user = await authService.linkAccount({
        pin: pin,
        email: email,
        token,
        platform: "web",
        device_id,
        provider: provider,
      });

      if (user.status === 200 || user.status === 201) {
        Cookies.set("session_id", user?.data?.data?.access_token, {
          domain: process.env.REACT_APP_PUBLIC_DOMAIN,
          path: "/",
          sameSite: "strict",
        });

        Cookies.set("device_id", user?.data?.data?.device_id, {
          domain: process.env.REACT_APP_PUBLIC_DOMAIN,
          path: "/",
        });

        setLoading(false);
        toast.success(t("Login Successful"));
        dispatch(profileUpdate(user.data));
        setstate1(false); // Close the modal
      }
    } catch (error) {
      console.log(error, "error verify");
      setLoading(false);
      if (error?.response?.data?.msg) {
        toast.error(error?.response?.data?.msg);
      } else if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      } else {
        console.log(error.response);
      }
    }
  };

  const resendCodeHandler = async () => {
    try {
      await authService.resendOTP({
        email: email,
      });
      toast.success(t("OTP resent to registered email"));
      setCountdown(60);
      setIsResending(true);
    } catch (error) {
      console.error("Error resending OTP:", error);
    }
  };

  const handlePaste = (e) => {
    const pastedData = e.clipboardData.getData("text");
    if (/^\d*$/.test(pastedData) && pastedData.length <= 4) {
      setPin(pastedData);
    } else {
      e.preventDefault();
    }
  };

  return (
    <div className="flex flex-col w-full text-center">
      <div className="flex items-center justify-center gap-2 mb-10">
        <div className="size-14 flex items-center justify-center bg-white shadow-14xl rounded-lg">
          <LogoMiniIcon />
        </div>
        <div className="text-grey-650">
          <PlusIcon />
        </div>
        <div className="size-14 flex items-center justify-center bg-white shadow-14xl rounded-lg">
          {provider === 'facebook' ? <FacebookDotIcon /> : <GoogleIcon />}
        </div>
      </div>
      <h3 className="!font-normal text-4xl text-secondary-1000 mb-1">
        {t("Link Accounts")}
      </h3>
      <p className="text-grey-800 text-sm font-normal text-center mb-3">
        {t("As: ")}
        <span className="text-primary font-normal text-sm">
          {`'${email ? email : "N/A"}'`}
        </span>
      </p>
      <p className="text-grey-800 text-sm font-normal text-center mb-8">
        {t(
          "We need your GoPDF password so we can link your google account. You'll only need to do this once. We've sent a 4-character code to"
        )}
        <span className="text-primary font-normal text-sm ml-1">
          {`'${email ? email : "N/A"}'`}
        </span>
        . {t("Please enter it soon, as it expires shortly.")}
      </p>

      <div className="grid grid-cols-4 flex-wrap mb-8 border border-secondary-30 rounded-lg divide-x divide-secondary-30">
        <PinField
          type="number"
          length={4}
          placeholder="0"
          validate={/^[0-9]$/}
          onPaste={handlePaste}
          onChange={(pin) => {
            setPin(pin);
          }}
          className="bg-transparent appearance-none overflow-y-auto text-secondary py-0.5 text-center md:text-3xl sm:text-2xl text-xl outline-none border-darkgrey focus:border-primary focus:shadow-primary"
        />
      </div>
      {pin && pin.length === 4 && !/^\d{4}$/.test(pin) && (
        <p className="text-red-500 text-sm mb-4">
          {t("Please enter a valid 4-digit PIN")}
        </p>
      )}
      <Button
        value={t("Verify")}
        className="mb-10 w-full"
        type="submit"
        isLoading={loading}
        disabled={loading || !pin || pin.length !== 4 || !/^\d{4}$/.test(pin)}
        onClick={() => {
          if (pin && pin.length === 4 && /^\d{4}$/.test(pin)) {
            handleVerifyOTP(pin);
          }
        }}
      />
      <p className="text-center text-secondary-400 text-base">
        {t("Didn't receive the code?")}{" "}
        {!isResending && (
          <a
            className="text-primary cursor-pointer font-semibold text-base"
            href="#"
            onClick={() => resendCodeHandler()}
          >
            {t("Resend")}
          </a>
        )}
        {isResending && (
          <span className="text-primary cursor-pointer font-semibold">
            {t("Resend in")} {countdown} {t("seconds")}
          </span>
        )}
      </p>
    </div>
  );
};

export default LinkAccountModal; 